/**
 * CUSTOMER-172B / COMMERCE-176C-R1 — suggestion list sibling of native input island.
 * Updating this tree MUST NOT remount/rerender SearchNativeInputIsland.
 * Human labels only — no technical kind chips / matchReason.
 */
import { memo, type ReactNode } from "react";
import type { StoreV2Lang } from "./store-v2-locale.js";
import type { SearchSuggestion } from "./search-v2.js";
import { suggestionThumbUrl } from "./search-thumb-v2.js";
import { formatUah } from "./money-v2.js";

type Props = {
  lang: StoreV2Lang;
  open: boolean;
  loading: boolean;
  suggestions: SearchSuggestion[];
  onPick: (s: SearchSuggestion) => void;
  onPickPointerDown: () => void;
  onShowAll?: () => void;
};

const MAX_VISIBLE = 5;

function SearchSuggestionSurfaceInner({
  lang,
  open,
  loading,
  suggestions,
  onPick,
  onPickPointerDown,
  onShowAll,
}: Props) {
  if (!open) return null;

  const visible = suggestions.slice(0, MAX_VISIBLE);
  const draftQuery = visible[0]?.query || suggestions[0]?.query || "";

  return (
    <div
      className="sv2-search-suggest sv2-search-suggest-compact"
      data-testid="sv2-search-suggest"
      role="listbox"
      aria-label={lang === "en" ? "Suggestions" : "Підказки"}
    >
      {loading ? (
        <div className="sv2-search-suggest-loading" data-testid="sv2-search-suggest-loading">
          <span className="sv2-search-spinner" aria-hidden="true" />
          {lang === "en" ? "Searching…" : "Шукаємо…"}
        </div>
      ) : suggestions.length === 0 ? (
        <div className="sv2-search-suggest-empty" data-testid="sv2-search-suggest-empty">
          {lang === "en" ? "No matches" : "Нічого не знайдено"}
        </div>
      ) : (
        <>
          <ul className="sv2-search-suggest-list">
            {visible.map((s) => {
              const thumb =
                s.kind === "product" || s.kind === "sku" || (s.kind === "brand" && s.imageUrl)
                  ? suggestionThumbUrl(s.imageUrl)
                  : null;
              const priceLabel =
                typeof s.priceUah === "number" && s.priceUah > 0 ? formatUah(s.priceUah) : null;
              return (
                <li key={`${s.kind}:${s.productId ?? s.query}`}>
                  <button
                    type="button"
                    className="sv2-search-suggest-item"
                    data-kind={s.kind}
                    role="option"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onPickPointerDown();
                    }}
                    onClick={() => onPick(s)}
                  >
                    {thumb ? (
                      <img
                        className="sv2-search-suggest-thumb"
                        src={thumb}
                        alt=""
                        width={44}
                        height={44}
                        loading="eager"
                        decoding="async"
                        onError={(ev) => {
                          (ev.currentTarget as HTMLImageElement).style.visibility = "hidden";
                        }}
                      />
                    ) : (
                      <span className="sv2-search-suggest-thumb sv2-search-suggest-thumb-empty" aria-hidden>
                        {s.kind === "brand" ? "🏷" : s.kind === "category" ? "📂" : "·"}
                      </span>
                    )}
                    <span className="sv2-search-suggest-meta">
                      <span className="sv2-search-suggest-label">{s.label}</span>
                      {s.brand && (s.kind === "product" || s.kind === "sku") ? (
                        <span className="sv2-search-suggest-brand">{s.brand}</span>
                      ) : null}
                      {priceLabel ? (
                        <span className="sv2-search-suggest-price">{priceLabel}</span>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {onShowAll || draftQuery ? (
            <button
              type="button"
              className="sv2-search-suggest-all"
              data-testid="sv2-search-suggest-all"
              onMouseDown={(e) => {
                e.preventDefault();
                onPickPointerDown();
              }}
              onClick={() => {
                if (onShowAll) onShowAll();
                else if (visible[0]) onPick({ ...visible[0]!, kind: "product", label: draftQuery, query: draftQuery });
              }}
            >
              {lang === "en" ? "Show all results →" : "Показати всі результати →"}
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}

export const SearchSuggestionSurface = memo(SearchSuggestionSurfaceInner);

export type { Props as SearchSuggestionSurfaceProps };
export type SearchSuggestionSurfaceSlot = ReactNode;
